import React, { useState, useRef, useId, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import useFocusTrap from '../hooks/useFocusTrap';

const STORAGE_KEY = 'onboardingTourSeen';

/**
 * OnboardingTour — İlk giriş (admin) için 5 adımlık kısa tanıtım turu.
 *
 * - Kullanıcı `admin` rolünde değilse veya flag set ise `null` döner.
 * - `localStorage.onboardingTourSeen` ile bir kez gösterilir.
 * - Tam ekran merkezi modal, mobilde alt sheet gibi davranır.
 * - ESC, Atla veya Bitir → flag set.
 */
export default function OnboardingTour() {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const titleId = useId();

  // İlk kontrol senkron — eligibility'yi flag + rol ile belirle.
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !localStorage.getItem(STORAGE_KEY);
  });
  const [step, setStep] = useState(0);

  // Auth yüklenene kadar bekle; admin değilse veya kapalıysa kapat.
  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== 'admin') setOpen(false);
  }, [loading, user]);

  const finish = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (_) { /* no-op */ }
    setOpen(false);
  };
  const handleSkip = finish;

  useFocusTrap(open, panelRef, { onEscape: handleSkip });

  if (loading || !user || user.role !== 'admin' || !open) return null;

  const steps = [
    {
      icon: '👋',
      title: t('tourStep1Title') || 'RestaFlex\'e Hoş Geldin!',
      body: t('tourStep1Body') || 'Yaklaşık 2 dakikalık kısa bir turla sana ana özelliklerin nerede olduğunu göstereceğiz. İstediğin an atlayabilirsin.',
      route: null,
    },
    {
      icon: '📊',
      title: t('tourStep2Title') || 'Dashboard',
      body: t('tourStep2Body') || 'Günlük ciro, sipariş ve canlı masa durumu burada özetlenir.',
      route: '/',
    },
    {
      icon: '🍽️',
      title: t('tourStep3Title') || 'Menü Yönetimi',
      body: t('tourStep3Body') || 'Ürünleri, kategorileri ve fiyatları buradan düzenleyebilirsin.',
      route: '/menu-management',
    },
    {
      icon: '🧾',
      title: t('tourStep4Title') || 'Masalar',
      body: t('tourStep4Body') || 'Açık masaları ve siparişlerini buradan yönetebilirsin.',
      route: '/tables',
    },
    {
      icon: '⚙️',
      title: t('tourStep5Title') || 'Ayarlar',
      body: t('tourStep5Body') || 'Restoran bilgilerini, kullanıcıları ve entegrasyonları buradan yapılandırabilirsin.',
      route: '/settings',
    },
  ];

  const isLast = step === steps.length - 1;
  const isFirst = step === 0;
  const current = steps[step];

  const goToRoute = () => {
    if (current.route) navigate(current.route);
    finish();
  };

  return (
    <div
      className="fixed inset-0 z-[190] flex flex-col justify-end md:items-center md:justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip} aria-hidden="true" />
      <div
        ref={panelRef}
        className="relative bg-white dark:bg-gray-900 rounded-t-3xl md:rounded-2xl shadow-2xl w-full md:max-w-lg md:mx-4 max-h-[92vh] flex flex-col"
      >
        {/* drag handle (mobile) */}
        <div className="flex justify-center pt-3 pb-1 md:hidden">
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        {/* Skip ✕ top-right */}
        <button
          type="button"
          onClick={handleSkip}
          aria-label={t('tourSkip') || 'Atla'}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm font-semibold z-10"
        >
          <span aria-hidden="true">✕</span>
        </button>

        {/* content */}
        <div className="px-6 pt-8 pb-4 text-center">
          <div className="text-5xl mb-4" aria-hidden="true">{current.icon}</div>
          <h2
            id={titleId}
            className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3"
          >
            {current.title}
          </h2>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
            {current.body}
          </p>

          {current.route && (
            <button
              type="button"
              onClick={goToRoute}
              className="mt-5 inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-semibold hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
            >
              {t('tourGoTo') || 'Sayfaya Git'} <span aria-hidden="true">→</span>
            </button>
          )}
        </div>

        {/* indicators */}
        <div className="flex justify-center gap-2 py-3">
          {steps.map((_, i) => (
            <span
              key={i}
              aria-hidden="true"
              className={`h-2 rounded-full transition-all ${
                i === step
                  ? 'w-6 bg-blue-600 dark:bg-blue-400'
                  : 'w-2 bg-gray-300 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        {/* footer nav */}
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
          <button
            type="button"
            onClick={handleSkip}
            className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-2"
          >
            {t('tourSkip') || 'Atla'}
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={() => setStep(s => Math.max(0, s - 1))}
                className="px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-200 dark:hover:bg-gray-700"
              >
                <span aria-hidden="true">←</span> {t('tourPrev') || 'Geri'}
              </button>
            )}
            {isLast ? (
              <button
                type="button"
                onClick={finish}
                className="px-5 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
              >
                <span aria-hidden="true">✓</span> {t('tourFinish') || 'Bitir'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))}
                className="px-5 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
              >
                {isFirst ? (t('tourGetStarted') || 'Hadi Başlayalım') : (t('tourNext') || 'İleri')} <span aria-hidden="true">→</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
